// components/LivestreamTable.jsx
import React from 'react';
import { FaEdit, FaEye, FaTag, FaUser, FaToggleOn, FaToggleOff, FaTrash } from 'react-icons/fa';

const LivestreamTable = ({ livestreams, onEdit, onDelete, onToggleStatus }) => {
  // Formatear precio
  const formatPrice = (price) => {
    return price > 0 
      ? `$${price.toLocaleString('es-CL')}` 
      : 'Gratis';
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No definido';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('es-CL', options);
  };

  // Obtener clase según estado
  const getStatusClass = (status) => {
    switch (status) {
      case 'EnVivo':
        return 'bg-red-100 text-red-800';
      case 'Próximo':
        return 'bg-blue-100 text-blue-800';
      case 'Finalizado':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg shadow">
      <table className="min-w-full bg-zinc-800 divide-y divide-zinc-700">
        <thead>
          <tr>
            <th className="px-6 py-3 bg-zinc-700 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Livestream
            </th>
            <th className="px-6 py-3 bg-zinc-700 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Detalles
            </th>
            <th className="px-6 py-3 bg-zinc-700 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Participantes
            </th>
            <th className="px-6 py-3 bg-zinc-700 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
              Estado
            </th>
            <th className="px-6 py-3 bg-zinc-700 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-700">
          {livestreams.length > 0 ? (
            livestreams.map((livestream) => (
              <tr key={livestream.livestreamId} className={`hover:bg-zinc-700 transition-colors ${!livestream.habilitado ? 'opacity-60' : ''}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-16 w-24 bg-zinc-700 rounded overflow-hidden">
                      <img 
                        src={livestream.imagen} 
                        alt={livestream.titulo} 
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150x100?text=Sin+Imagen';
                        }}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-white">{livestream.titulo}</div>
                      <div className="text-xs text-gray-400">
                        Precio: {formatPrice(livestream.precio)}
                      </div>
                      <div className="text-xs text-gray-400">
                        Vistas: {livestream.vistas}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-white">
                    <div className="text-xs text-gray-400">
                      <span className="font-medium text-gray-300">Fecha:</span> {formatDate(livestream.fecha)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                      {livestream.descripcion}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {livestream.tags && livestream.tags.map((tag) => (
                        <span 
                          key={tag.tagId} 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" 
                          style={{ backgroundColor: `${tag.colorHex}30`, color: tag.colorHex }}
                        >
                          <FaTag className="mr-1 text-xs" />
                          {tag.nombre}
                        </span>
                      ))}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-1">
                    {livestream.comediantes && livestream.comediantes.map((comediante, index) => (
                      <div key={index} className="flex items-center text-sm text-white">
                        <FaUser className="mr-1 text-xs text-gray-400" />
                        {comediante.nombre}
                        {comediante.esPrincipal && (
                          <span className="ml-1 text-xs text-red-400">(Principal)</span>
                        )}
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex flex-col items-center space-y-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                      ${getStatusClass(livestream.estado)}`}
                    >
                      {livestream.estado}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                      ${livestream.habilitado 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'}`}
                    >
                      {livestream.habilitado ? 'Habilitado' : 'Deshabilitado'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => onEdit(livestream)}
                      className="p-1 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                      title="Editar livestream"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => onToggleStatus(livestream)}
                      className={`p-1 rounded ${livestream.habilitado 
                        ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                        : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                      title={livestream.habilitado ? "Deshabilitar livestream" : "Habilitar livestream"}
                    >
                      {livestream.habilitado ? <FaToggleOff /> : <FaToggleOn />}
                    </button>
                    <button
                      onClick={() => onDelete(livestream.livestreamId, livestream.titulo)}
                      className="p-1 rounded bg-red-100 text-red-600 hover:bg-red-200"
                      title="Eliminar livestream"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                No se encontraron livestreams que coincidan con la búsqueda.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LivestreamTable;